"use client";

import { Alert } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";

export interface OccupiedStage {
  id: string;
  name: string;
}

export interface RemoteStageChoice {
  remote_stage_id: string;
  name: string;
}

export interface ShapeDiff {
  local_only: string[];
  remote_only: string[];
  order_changed: boolean;
}

interface StageMapStepProps {
  occupiedStages: OccupiedStage[];
  remoteStages: RemoteStageChoice[];
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  complete: boolean;
  shapeMismatch?: boolean;
  shapeDiff?: ShapeDiff | null;
  shapeAcknowledged?: boolean;
  onShapeAcknowledgedChange?: (v: boolean) => void;
  requireShapeAck?: boolean;
  title?: string;
}

/**
 * Auto-suggest a `candidate_stage_map` by case-insensitive name match between
 * the job's occupied local stages and the ATS's remote stages. Only exact
 * (trimmed, case-insensitive) name matches are pre-filled — everything else
 * is left for the user to pick explicitly.
 */
export function suggestStageMap(
  occupied: { id: string; name: string }[],
  remote: { remote_stage_id: string; name: string }[],
): Record<string, string> {
  const byName = new Map(
    remote.map((r) => [r.name.trim().toLowerCase(), r.remote_stage_id]),
  );
  const out: Record<string, string> = {};
  for (const stage of occupied) {
    const hit = byName.get(stage.name.trim().toLowerCase());
    if (hit) out[stage.id] = hit;
  }
  return out;
}

/**
 * Reactive stage-mapping step shown after a link submit 409s
 * STAGE_MAP_REQUIRED / STAGE_SHAPE_ACK_REQUIRED. One row per occupied local
 * stage (candidates already sitting in it) with a Select of remote ATS stages.
 * When `requireShapeAck && shapeMismatch`, also shows a shape-diff banner and
 * acknowledgment checkbox. `complete` is computed by the caller and only used
 * here to render a lightweight progress note — the caller owns the submit gate.
 */
export function StageMapStep({
  occupiedStages,
  remoteStages,
  value,
  onChange,
  complete,
  shapeMismatch,
  shapeDiff,
  shapeAcknowledged = false,
  onShapeAcknowledgedChange,
  requireShapeAck,
  title,
}: StageMapStepProps) {
  const hasOccupied = occupiedStages.length > 0;
  const showShapeAck = !!(requireShapeAck && shapeMismatch);
  const mappedCount = occupiedStages.filter((s) => !!value[s.id]).length;

  const intro =
    title ??
    (hasOccupied
      ? "These stages already have candidates in them. Map each to a stage in the ATS before linking so they land in the right place."
      : "Review pipeline changes");

  function handleSelect(stageId: string, remoteStageId: string) {
    onChange({ ...value, [stageId]: remoteStageId });
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-contrast/70">{intro}</p>

      {showShapeAck && shapeDiff && (
        <Alert.Container variant="warning" dir="vertical">
          <Alert.Title>Pipeline stages don’t match</Alert.Title>
          <Alert.Content>
            <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-4 text-sm text-contrast/80">
              {shapeDiff.local_only.length > 0 && (
                <li>
                  Removing from Arbi: {shapeDiff.local_only.join(", ")}
                </li>
              )}
              {shapeDiff.remote_only.length > 0 && (
                <li>
                  Adding from ATS: {shapeDiff.remote_only.join(", ")}
                </li>
              )}
              {shapeDiff.order_changed && (
                <li>Stage order will change to match the ATS job</li>
              )}
            </ul>
          </Alert.Content>
        </Alert.Container>
      )}

      {showShapeAck && (
        <label className="flex items-center gap-2 text-sm text-heading">
          <Checkbox
            checked={shapeAcknowledged}
            onChange={(checked) => onShapeAcknowledgedChange?.(checked)}
            aria-label="I understand the pipeline stages will change to match the ATS job"
          />
          I understand the pipeline stages will change to match the ATS job
        </label>
      )}

      {hasOccupied && (
        <>
          <div className="flex flex-col gap-2">
            {occupiedStages.map((stage) => {
              const selected = remoteStages.find(
                (rs) => rs.remote_stage_id === value[stage.id],
              );
              return (
                <div
                  key={stage.id}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-sm font-medium text-heading">
                    {stage.name}
                  </span>
                  <Select.Root>
                    <Select.Trigger
                      className="w-56"
                      active={!!value[stage.id]}
                    >
                      <Select.TriggerContent>
                        {selected?.name ?? "Map to ATS stage…"}
                      </Select.TriggerContent>
                      <Select.Icon />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.List>
                        {remoteStages.map((rs) => (
                          <Select.Item
                            key={rs.remote_stage_id}
                            selected={
                              value[stage.id] === rs.remote_stage_id
                            }
                            onSelect={() =>
                              handleSelect(stage.id, rs.remote_stage_id)
                            }
                          >
                            <Select.ItemLabel>{rs.name}</Select.ItemLabel>
                            {value[stage.id] === rs.remote_stage_id && (
                              <Select.ItemCheck />
                            )}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Content>
                  </Select.Root>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-contrast/50">
            {complete
              ? "All stages mapped."
              : `${mappedCount}/${occupiedStages.length} mapped`}
          </p>
        </>
      )}
    </div>
  );
}
